import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { toJS } from 'mobx';
import { ICategorySFilter } from 'src/@types/SearchFilterTypes';
import CDataGridPro from 'src/components/CDataGridPro';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol, { ICategoryGridRow } from 'src/hooks/useGetGridCol';
import {
  ContentBox,
  ContentBoxButtons,
  ContentButtonAction,
  ContentButtonCreate,
} from 'src/layouts/form/FormButton';
import { ICategory, useStores } from 'src/models';
import { categoryApi } from 'src/service';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';

import { OrderEditDialog } from '../components/OrderEditDialog';
import { OrderEditType } from '../components/orderEditTypes';

import { CategoryFormDialog } from './CategoryFormDialog';

interface ICategoryGridProps {
  searchData: Partial<ICategorySFilter>;
}

export const CategoryGrid = ({ searchData }: ICategoryGridProps) => {
  const { t } = useTranslation();
  const { categoryStore } = useStores();
  const { getCategryGridCol } = useGetGridCol();
  const { callApiWithLoading } = useCallApiWithLoading();
  const { openDialog, closeDialog } = useDialogContext();

  // 그리드 row 데이터
  const [gridRows, setGridRows] = useState<ICategory[]>([]);

  // 목록 조회
  const getCategoryData = useCallback(async () => {
    await categoryStore.getCategoryTree(searchData).then(() => {
      const currentLang = localStorage.getItem('i18nextLng') || 'ko';
      // 재귀적으로 카테고리 트리를 평면화하는 함수
      const flattenCategoryTree = (
        category: ICategory,
        parentHierarchy: string[] = [],
      ): ICategory[] => {
        // 현재 언어에 맞는 카테고리 정보 찾기
        const categoryInfo =
          category.categoryInfoList?.find((info) => info.langCd === currentLang) ||
          category.categoryInfoList?.[0]; // 현재 언어가 없으면 첫 번째 언어 사용
        const currentHierarchy = [...parentHierarchy, categoryInfo?.categoryNm || '-'];

        const current = {
          ...category,
          hierarchy: currentHierarchy,
          id: category.categorySid,
        };

        const children =
          category.children?.flatMap((child) => flattenCategoryTree(child, currentHierarchy)) || [];

        return [current, ...children];
      };

      if (categoryStore.categoryTree) {
        const newDataGrid = toJS(categoryStore.categoryTree).flatMap((category) =>
          flattenCategoryTree(category),
        );

        setGridRows(newDataGrid);
      }
    });
  }, [searchData]);

  useEffect(() => {
    getCategoryData();
  }, [getCategoryData]);

  // 등록 버튼: 등록 dialog open
  const handleAddCategory = () => {
    openDialog({
      id: FORM_IDS.CATEGORY,
      formType: FormType.CREATE,
      dialogContent: (
        <CategoryFormDialog formType={FormType.CREATE} onSubmitSuccess={handleSubmitSuccess} />
      ),
    });
  };

  // 그리드 더블 클릭 시: 수정 dialog open
  const handleRowDoubleClick = (param: GridRowParams<ICategoryGridRow>) => {
    callApiWithLoading(async () => {
      await categoryApi.getDetail(param.row.categorySid).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.CATEGORY,
            formType: FormType.UPDATE,
            dialogContent: (
              <CategoryFormDialog
                formType={FormType.UPDATE}
                onSubmitSuccess={handleSubmitSuccess}
                selectedData={res.data as ICategory}
              />
            ),
          });
        }
      });
    });
  };

  // 등록 & 수정 성공 시
  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.CATEGORY);
    getCategoryData();
  };

  // 순서 변경 모드 토글
  const handleOrderChange = () => {
    openDialog({
      id: FORM_IDS.CATEGORY_ORDER,
      formType: FormType.UPDATE,
      maxWidth: 'xl',
      dialogContent: <OrderEditDialog type={OrderEditType.CATEGORY} />,
      setOnClose: () => {
        closeDialog(FORM_IDS.CATEGORY_ORDER);
        getCategoryData();
      },
    });
  };

  return (
    <ContentBox>
      <ContentBoxButtons>
        {/* 순서 변경 */}
        <ContentButtonAction onClick={handleOrderChange}>
          {t('common.button.orderChange')}
        </ContentButtonAction>
        {/* 등록 */}
        <ContentButtonCreate onClick={handleAddCategory}>
          {t('common.button.register')}
        </ContentButtonCreate>
      </ContentBoxButtons>

      {/* 그리드 */}
      <CDataGridPro
        columns={getCategryGridCol()}
        rows={gridRows}
        slots={{ footer: () => null }}
        rowCount={gridRows.length}
        onRowDoubleClick={handleRowDoubleClick}
        treeData // 트리 데이터 사용
        getRowId={(row) => `${row.id}`} // 고유 ID를 정의
        getTreeDataPath={(row) => row.hierarchy} // 트리 데이터 경로 hierarchy로 정의
        defaultGroupingExpansionDepth={-1} // 모두 확장되어 표시되도록
        groupingColDef={{
          headerName: '카테고리 이름',
          headerAlign: 'center',
          align: 'center',
          flex: 2,
          hideDescendantCount: true, // 자식 노드 수 숨기기
        }}
      />
    </ContentBox>
  );
};
